import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { Star, ArrowRight } from "lucide-react";
import type { HomepageArticle } from "@/components/homepage/types";

interface EditorsPicksSectionProps {
  items: HomepageArticle[];
  isLoading: boolean;
}

export function EditorsPicksSection({ items, isLoading }: EditorsPicksSectionProps) {
  const displayItems = items.length > 0 ? items.slice(0, 4) : [];

  if (!displayItems.length && !isLoading) return null;

  return (
    <section className="border-b border-border bg-neutral-50/50" aria-labelledby="editors-picks-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Editor's Choice"
          icon={Star}
          title="Editor's Picks"
          href="/knowledge-hub"
        />
        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-48 bg-neutral-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : displayItems.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {displayItems.map((item, idx) => (
              <Link key={item.id} href={`/news/${item.id}`} className="group block">
                <Card className="hover:border-neutral-300 transition-all duration-150">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-display text-2xl font-semibold text-neutral-200 tabular-nums leading-none">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="eyebrow">Editor's Pick</span>
                    </div>
                    <h3 className="text-sm font-heading font-semibold leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2">
                      {item.title}
                    </h3>
                    <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{item.author}</span>
                      <span className="text-neutral-300">·</span>
                      <span>{item.createdAt}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState icon={Star} description="No editor's picks yet." />
        )}
      </div>
    </section>
  );
}